<template>
  <div ref="root" class="more" tabindex="-1" :aria-label="subGroup ? subGroup.title : 'Mehr'">
    <!-- zweite Ebene: Kinder einer Gruppe aus „Weitere Bereiche“ (Titel und Zurück stehen in der App-Leiste) -->
    <ul v-if="subGroup" class="more__list">
      <li v-for="entry in subGroup.children" :key="entry.path">
        <router-link
          :to="entry.path"
          class="more__row"
          :aria-current="isActivePath(entry.path) ? 'page' : null"
          @click.native="$emit('navigate')"
        >
          <span class="more__icon" aria-hidden="true"><app-icon :name="entry.icon" :size="20" /></span>
          <span class="more__text">
            <span class="more__label">{{ entry.title }}</span>
            <app-icon name="chevR" :size="16" class="more__trail" />
          </span>
        </router-link>
      </li>
    </ul>

    <template v-else>
      <router-link to="/settings/profile" class="more__user" @click.native="$emit('navigate')">
        <app-avatar :name="name" :size="40" />
        <span class="more__who">
          <span class="more__name">{{ name }}</span>
          <span v-if="login" class="more__sub">{{ login }}</span>
          <span v-if="subline" class="more__sub">{{ subline }}</span>
        </span>
        <app-icon name="chevR" :size="16" class="more__trail" />
      </router-link>

      <section v-for="section in sections" :key="section.id" class="more__section">
        <h2 class="more__heading">{{ section.title }}</h2>
        <ul class="more__list">
          <li v-for="row in section.rows" :key="row.key">
            <router-link
              v-if="row.path"
              :to="row.path"
              class="more__row"
              :aria-current="isActivePath(row.path) ? 'page' : null"
              @click.native="$emit('navigate')"
            >
              <span class="more__icon" aria-hidden="true"><app-icon :name="row.icon" :size="20" /></span>
              <span class="more__text">
                <span class="more__label">{{ row.title }}</span>
                <app-icon name="chevR" :size="16" class="more__trail" />
              </span>
            </router-link>
            <button v-else type="button" class="more__row" @click="openGroup(row.id)">
              <span class="more__icon" aria-hidden="true"><app-icon :name="row.icon" :size="20" /></span>
              <span class="more__text">
                <span class="more__label">{{ row.title }}</span>
                <app-icon name="chevR" :size="16" class="more__trail" />
              </span>
            </button>
          </li>
        </ul>
      </section>

      <ul class="more__list">
        <li>
          <button type="button" class="more__row" @click="showMessages">
            <span class="more__icon more__icon--bell" aria-hidden="true">
              <app-icon name="bell" :size="20" />
              <span v-if="unreadMessages" class="more__dot"></span>
            </span>
            <span class="more__text">
              <span class="more__label">Nachrichten<span v-if="unreadMessages" class="app-sr-only"> (ungelesen)</span></span>
              <app-icon name="chevR" :size="16" class="more__trail" />
            </span>
          </button>
        </li>
        <li v-if="canSwitchTenant">
          <button type="button" class="more__row" @click="switchTenant">
            <span class="more__icon" aria-hidden="true"><app-icon name="briefcase" :size="20" /></span>
            <span class="more__text">
              <span class="more__label">Mandant wechseln</span>
              <app-icon name="chevR" :size="16" class="more__trail" />
            </span>
          </button>
        </li>
        <li>
          <a :href="mobilityHubUrl" class="more__row">
            <span class="more__icon" aria-hidden="true"><img src="/img/sima-star.png" alt="" width="20" height="20" /></span>
            <span class="more__text">
              <span class="more__label">Zu Mobility Hub wechseln</span>
              <app-icon name="chevR" :size="16" class="more__trail" />
            </span>
          </a>
        </li>
        <li>
          <a :href="docsUrl" target="_blank" rel="noopener noreferrer" class="more__row">
            <span class="more__icon" aria-hidden="true"><app-icon name="bookOpen" :size="20" /></span>
            <span class="more__text">
              <span class="more__label">Kundendokumentation<span class="app-sr-only"> (öffnet in neuem Tab)</span></span>
              <app-icon name="external" :size="16" class="more__trail" />
            </span>
          </a>
        </li>
        <li>
          <button type="button" class="more__row more__row--logout" @click="$emit('sign-out')">
            <span class="more__icon" aria-hidden="true"><app-icon name="logout" :size="20" /></span>
            <span class="more__text"><span class="more__label">Abmelden</span></span>
          </button>
        </li>
      </ul>
      <p class="more__foot">SIMA.Ticketing Verwaltung · Version {{ version }}</p>
    </template>
  </div>
</template>

<script>
// Handy: Mehr-Menü als Ebene in der Shell, ohne eigene Route (Spezifikation 2.11, gen_r3.py:1619–1660,
// Screenshot e-Mobil-Menue-390): Benutzerkarte (Name, darunter Benutzername wie in der Seitenleiste, dann „Rolle ·
// Mandant“), Abschnitte Verwaltung · Auswertungen · Weitere Bereiche,
// „Mandant wechseln“ (alle angemeldeten Benutzer, Dialog über $shell.tenantSwitch), „Zu Mobility Hub wechseln“ (selbes Fenster),
// Kundendokumentation, Abmelden, Fuß mit Version. „Weitere Bereiche ›“ öffnet die zweite Ebene ($shell.moreGroup).
// Ereignisse: navigate (ein Ziel wurde angetippt; die Shell schließt das Menü), sign-out.
import {
  normalizePath,
  roleLabel,
  userDisplayName,
  userLoginName,
  customerDisplayName,
  tenantSwitchAllowed,
  DOCS_URL,
  MOBILITY_HUB_URL
} from '../../shell/navigation';
import { MESSAGES, messages, isRead, openMessage } from '../../shell/messages';

// Gruppen, deren Seiten direkt als Abschnitt erscheinen; alle übrigen Gruppen stehen unter „Weitere Bereiche“
const DIRECT = ['verwaltung', 'auswertungen'];

export default {
  name: 'AppMoreMenu',
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' }
  },
  data() {
    return { docsUrl: DOCS_URL, mobilityHubUrl: MOBILITY_HUB_URL, version: process.env.VUE_APP_VERSION };
  },
  computed: {
    // ungelesene Nachrichten (src/shell/messages.js; messages.read reaktiv gelesen)
    unreadMessages() {
      return messages.read && MESSAGES.some(m => !isRead(m.id));
    },
    profile() {
      return this.$store.state.profile;
    },
    canSwitchTenant() {
      return tenantSwitchAllowed(this.$store.state);
    },
    name() {
      return userDisplayName(this.profile);
    },
    // Benutzername unter dem Namen (entfällt, wenn er schon als Name dient)
    login() {
      return userLoginName(this.profile);
    },
    // „Rolle · Kundenname“, ohne bekannte Rolle nur der Kundenname
    subline() {
      const parts = [roleLabel(this.profile ? this.profile.role : null), customerDisplayName(this.$store.state)];
      return parts.filter(Boolean).join(' · ');
    },
    sections() {
      const sections = [];
      DIRECT.forEach((id) => {
        const group = this.groups.find(g => g.id === id && g.children);
        if (group) {
          sections.push({
            id,
            title: group.title,
            rows: group.children.map(entry => ({ key: entry.path, path: entry.path, title: entry.title, icon: entry.icon }))
          });
        }
      });
      const others = this.groups.filter(g => g.children && DIRECT.indexOf(g.id) === -1);
      if (others.length) {
        sections.push({
          id: 'weitere',
          title: 'Weitere Bereiche',
          rows: others.map(group => ({ key: group.id, id: group.id, title: group.title, icon: group.icon }))
        });
      }
      return sections;
    },
    subGroup() {
      const id = this.$shell ? this.$shell.moreGroup : null;
      return id ? (this.groups.find(g => g.id === id && g.children) || null) : null;
    }
  },
  watch: {
    // Ebenenwechsel: oben beginnen, Fokus in das Menü (Screenreader)
    subGroup() {
      this.$nextTick(this.focusTop);
    }
  },
  mounted() {
    this.focusTop();
  },
  methods: {
    isActivePath(path) {
      return normalizePath(path) === this.activePath;
    },
    // Menü schließen (wie nach einem Ziel), dann den Dialog öffnen
    switchTenant() {
      this.$emit('navigate');
      this.$shell.tenantSwitch = true;
    },
    // Nachrichten (Kundenwunsch 13.10.2026): neueste Nachricht im Dialog öffnen
    showMessages() {
      this.$emit('navigate');
      openMessage(MESSAGES[0].id);
    },
    openGroup(id) {
      this.$shell.moreGroup = id;
    },
    focusTop() {
      window.scrollTo(0, 0);
      if (this.$refs.root) {
        try {
          this.$refs.root.focus({ preventScroll: true });
        } catch (e) {
          this.$refs.root.focus();
        }
      }
    }
  }
};
</script>

<style scoped>
.more {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 12px calc(12px + var(--shell-unten));
  color: var(--ink);
  font-family: var(--font);
}
.more:focus {
  outline: none;
}

/* Benutzerkarte */
.more__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  color: var(--ink);
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}
.more__user:active {
  background: var(--press);
}
.more__user:focus-visible {
  outline-offset: 2px;
}
.more__who {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.more__name {
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
}
.more__sub {
  font-size: 13px;
  line-height: 18px;
  color: var(--ink-3);
}
.more__name,
.more__sub {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Abschnitte */
.more__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
h2.more__heading {
  margin: 0;
  padding: 0 12px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: var(--ink-3);
}
.more__list {
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  overflow: hidden;
}

/* Zeile 52: Icon 20 in 30-px-Platz, Textbereich mit Linie oben (nicht in der ersten Zeile) */
.more__row {
  width: 100%;
  min-height: 52px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 0 0 0 12px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.more__row:active {
  background: var(--press);
}
/* Behälter schneidet ab (overflow hidden): Fokusring innen */
.more__row:focus-visible {
  outline-offset: -2px;
}
.more__icon {
  width: 30px;
  height: 30px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-3);
}
.more__text {
  flex: 1 1 auto;
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
}
.more__list > li + li .more__text {
  border-top: 1px solid var(--line-2);
}
.more__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
}
.more__trail {
  flex: none;
  color: var(--ink-3);
}
.more__row.more__row--logout,
/* Nachrichten: oranger Punkt an der Glocke, solange etwas ungelesen ist */
.more__icon--bell {
  position: relative;
}
.more__dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--acc);
  box-shadow: 0 0 0 2px var(--surface);
}
.more__row--logout .more__icon {
  color: var(--red);
}

p.more__foot {
  margin: 0;
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
</style>
